import { getCameraZonesList, getZoneId } from 'src/components/Camera/helpers/camera-zone-helpers';
import {
	CAMERA_BOOLEAN_VALUES,
	CAMERA_COND_NAMES,
	getCameraZoneName,
	isMotionCameraZoneComponent,
	normalizeCameraConditionValue,
} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export const CAMERA_MODE_MOTION_ZONES = 'motion_zones';

type CameraConditionComponent = {
	title: string;
	props: {
		id: string;
		values: string[];
		i18tr: string;
		add_info_ind?: string;
	};
};

export type CameraMotionZone = {
	id: string;
	title: string;
	value: string;
};

export type CameraDeviceProp = {
	title: string;
	value: string;
	disabled: boolean;
};

export type CameraConditionResult = {
	conditionName: string;
	conditionValue: string;
	conditionOperator: string;
	zoneAddInfoInd?: string;
};

export type CameraInitializationResult = {
	selectedCameraMode: string;
	selectedZone: string;
	selectedValue: string;
};

const buildBooleanCameraCondition = (
	title: string,
	id: string,
	i18tr: string,
): CameraConditionComponent => ({
	title,
	props: {
		id,
		values: [...CAMERA_BOOLEAN_VALUES],
		i18tr,
	},
});

/**
 * Build the list of scene condition components available for a Shelly Camera device.
 * Zone conditions are handled by CameraDeviceCondition (two-step UX).
 */
export const buildCameraConditions = (
	deviceSettings?: DeviceSettingsG2 | null,
): CameraConditionComponent[] => {
	void deviceSettings;
	return [
		buildBooleanCameraCondition('collapse_title_motion', CAMERA_COND_NAMES.MOTION, 'camera_motion'),
		buildBooleanCameraCondition('camera_privacy_mode', CAMERA_COND_NAMES.PRIVACY, 'camera_privacy'),
		buildBooleanCameraCondition('camera_arm_disarm', CAMERA_COND_NAMES.ARM, 'camera_arm'),
	];
};

/**
 * Load motion zones from device settings for the zone picker.
 */
export const getCameraMotionZones = (
	deviceId: string | undefined,
	deviceSettings: Record<string, DeviceSettingsG2 | undefined>,
): CameraMotionZone[] => {
	if (!deviceId) {
		return [];
	}

	const realDeviceId = deviceId.split('_')[0];
	const settings = deviceSettings[realDeviceId];
	if (!settings) {
		return [];
	}

	const zones: CameraMotionZone[] = [];
	const zoneComponents = getCameraZonesList(settings);

	for (let i = 0; i < zoneComponents.length; i++) {
		const componentKey = zoneComponents[i];
		if (!isMotionCameraZoneComponent(componentKey, settings)) {
			continue;
		}

		const zoneId = getZoneId(componentKey);
		zones.push({
			id: String(zoneId),
			title: getCameraZoneName(zoneId, settings),
			value: `zone_${zoneId}`,
		});
	}

	zones.sort((a, b) => Number(a.id) - Number(b.id));
	return zones;
};

/**
 * Top-level property tokens shown in CameraDeviceCondition.
 */
export const getEffectiveCameraDeviceProps = (
	formatFn: (key: string, opts?: { default?: string }) => string,
	motionZones: CameraMotionZone[],
): CameraDeviceProp[] => {
	return [
		{
			title: formatFn('collapse_title_motion', { default: 'Motion Detection' }),
			value: CAMERA_COND_NAMES.MOTION,
			disabled: false,
		},
		{
			title: formatFn('camera_privacy_mode', { default: 'Privacy Mode' }),
			value: CAMERA_COND_NAMES.PRIVACY,
			disabled: false,
		},
		{
			title: formatFn('camera_arm_disarm', { default: 'Arm / Disarm' }),
			value: CAMERA_COND_NAMES.ARM,
			disabled: false,
		},
		{
			title: formatFn('camera_motion_zones', { default: 'Motion zones' }),
			value: CAMERA_MODE_MOTION_ZONES,
			disabled: motionZones.length === 0,
		},
	];
};

export const validateCameraCondition = (
	selectedCameraMode: string,
	selectedZone: string,
	selectedValue: string,
): boolean => {
	if (!selectedCameraMode || !selectedValue) {
		return false;
	}

	if (selectedCameraMode === CAMERA_MODE_MOTION_ZONES) {
		return Boolean(selectedZone);
	}

	return true;
};

/**
 * Map wizard selections to DeviceConditionUI fields.
 */
export const buildCameraCondition = (
	selectedCameraMode: string,
	selectedZone: string,
	selectedValue: string,
): CameraConditionResult | null => {
	if (!selectedCameraMode || !selectedValue) {
		return null;
	}

	if (selectedCameraMode === CAMERA_MODE_MOTION_ZONES) {
		if (!selectedZone) {
			return null;
		}

		const zoneId = selectedZone.replace('zone_', '');
		return {
			conditionName: CAMERA_COND_NAMES.ZONE_MOTION,
			conditionValue: selectedValue,
			conditionOperator: 'eq',
			zoneAddInfoInd: zoneId,
		};
	}

	if (
		selectedCameraMode === CAMERA_COND_NAMES.MOTION ||
		selectedCameraMode === CAMERA_COND_NAMES.PRIVACY ||
		selectedCameraMode === CAMERA_COND_NAMES.ARM
	) {
		return {
			conditionName: selectedCameraMode,
			conditionValue: selectedValue,
			conditionOperator: 'eq',
		};
	}

	return null;
};

/**
 * Restore CameraDeviceCondition local state when editing an existing condition.
 */
export const initializeCameraDeviceFromCondition = (sceneData: {
	_device_condition?: { cond_name?: string; value?: string; add_info_ind?: string | number };
}): CameraInitializationResult | null => {
	const condition = sceneData._device_condition;
	if (!condition?.cond_name) {
		return null;
	}

	const normalizedValue = normalizeCameraConditionValue(condition.value);

	if (condition.cond_name === CAMERA_COND_NAMES.ZONE_MOTION) {
		const zoneId = condition.add_info_ind;
		if (zoneId === undefined || zoneId === null) {
			return null;
		}

		return {
			selectedCameraMode: CAMERA_MODE_MOTION_ZONES,
			selectedZone: `zone_${zoneId}`,
			selectedValue: normalizedValue,
		};
	}

	if (
		condition.cond_name === CAMERA_COND_NAMES.MOTION ||
		condition.cond_name === CAMERA_COND_NAMES.PRIVACY ||
		condition.cond_name === CAMERA_COND_NAMES.ARM
	) {
		return {
			selectedCameraMode: condition.cond_name,
			selectedZone: '',
			selectedValue: normalizedValue,
		};
	}

	return null;
};
